import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Search,
    ChevronDown,
    ChevronUp,
    Grid,
    List,
    ShoppingCart,
    Package,
    X,
    SlidersHorizontal,
    Check
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import Pagination from './Pagination';
import useIngramCatalog from '../../hooks/useIngramCatalog';
import useProductImage from '../../hooks/useProductImage';
import { useCart } from '../../context/CartContext';
import BrandPlaceholder from './BrandPlaceholder';
import { getCategoryGroup } from '../../data/productCategories';
import './ModernAccessoriesCatalog.scss';

/**
 * Per-card image. If the /search response already contained a cached
 * imageUrl (single-wave load path), use it immediately and skip the
 * per-card fetch. Otherwise lazy-fetch through the scraper cascade.
 * When no image can be resolved, show the clean BrandPlaceholder — never
 * an ugly SVG or `via.placeholder.com` external graphic.
 */
// Only shows a discount when MSRP is genuinely higher than the customer
// price. Where our 12% markup pushes the customer price above Ingram's
// retailPrice, we return 0 so no misleading strikethrough or badge renders.
function computeDiscountPct(product) {
    const priceNum = Number(product?.priceNumeric) || 0;
    const msrpNum = product?.msrp
        ? Number(String(product.msrp).replace(/[^\d.]/g, ''))
        : 0;
    if (msrpNum <= priceNum || priceNum <= 0) return 0;
    return Math.round(((msrpNum - priceNum) / msrpNum) * 100);
}

function ProductCardImage({ product }) {
    const containerRef = useRef(null);
    const [imgError, setImgError] = useState(false);
    const { imageUrl } = useProductImage({
        partNumber: product.partNumber,
        brand: product.manufacturer,
        upc: product.upc,
        name: product.name,
        ingramPartNumber: product.ingramPartNumber || product.id,
        initialImageUrl: product.imageUrl,   // from /search when cached
        containerRef,
    });
    const finalUrl = imageUrl || product.imageUrl;
    const showPlaceholder = !finalUrl || imgError;
    const discountPct = computeDiscountPct(product);

    return (
        <div
            ref={containerRef}
            className="jk-catalog__product-image"
        >
            {showPlaceholder ? (
                <BrandPlaceholder brand={product.manufacturer} name={product.name} category={product.category} size="card" />
            ) : (
                <img
                    src={finalUrl}
                    alt={product.name}
                    onError={() => setImgError(true)}
                />
            )}
            {discountPct > 0 && (
                <span className="jk-catalog__product-discount">-{discountPct}% OFF</span>
            )}
            {!product.inStock && (
                <span className="jk-catalog__product-badge jk-catalog__product-badge--out">
                    Out of Stock
                </span>
            )}
        </div>
    );
}

const ModernAccessoriesCatalog = () => {
    // Use the Ingram catalog hook for data management
    const {
        products,
        categories,
        brands,
        currentPage,
        totalPages,
        totalProducts,
        searchTerm,
        isLoading,
        error,
        handleSearch,
        handleCategoryChange,
        handleBrandChange,
        handlePageChange,
        resetFilters,
        retry,
        selectedCategory
    } = useIngramCatalog();

    // Cart functionality
    const { addItem, isInCart } = useCart();

    // Handle add to cart
    const handleAddToCart = (e, product) => {
        e.preventDefault(); // Prevent Link navigation
        e.stopPropagation();
        if (product.inStock) {
            addItem(product);
        }
    };

    // Local UI state
    const [viewMode, setViewMode] = useState('grid');
    const [showFilters, setShowFilters] = useState(false);
    const [expandedFilters, setExpandedFilters] = useState({
        categories: true,
        brands: true
    });

    // Track selected filters for UI (using arrays for multi-select compatibility)
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);

    // Keep the sidebar category checkbox in sync with the active category.
    // selectedCategory is a comma-joined string when multi-select is active
    // ("Cisco,Dell") — split it back to an array so checkbox state matches.
    // Without split(), the second selection wrote ["Cisco,Dell"] as one
    // item and broke subsequent add/remove toggles.
    useEffect(() => {
        setSelectedCategories(selectedCategory ? String(selectedCategory).split(',').map((s) => s.trim()).filter(Boolean) : []);
    }, [selectedCategory]);

    // True multi-select — toggle add/remove. Downstream (hook + backend)
    // handles the multi-vendor / multi-category case by calling Ingram once
    // per filter and merging results.
    const handleCategorySelect = (categoryName) => {
        const newSelection = selectedCategories.includes(categoryName)
            ? selectedCategories.filter(c => c !== categoryName)
            : [...selectedCategories, categoryName];
        setSelectedCategories(newSelection);
        handleCategoryChange(newSelection);
    };

    const handleBrandSelect = (brandName) => {
        const newSelection = selectedBrands.includes(brandName)
            ? selectedBrands.filter(b => b !== brandName)
            : [...selectedBrands, brandName];
        setSelectedBrands(newSelection);
        handleBrandChange(newSelection);
    };

    const handleResetFilters = () => {
        setSelectedCategories([]);
        setSelectedBrands([]);
        resetFilters();
    };

    const activeFiltersCount = selectedCategories.length + selectedBrands.length;

    // Breadcrumbs (PageHeader prepends Home): Products > [Group] > [Category].
    // When no category is active, the current crumb is "All Products".
    const catalogBreadcrumbs = (() => {
        const crumbs = [{ label: 'Products', path: '/accessories' }];
        if (selectedCategory) {
            const group = getCategoryGroup(selectedCategory);
            if (group) crumbs.push({ label: group }); // group heading (no listing route)
            crumbs.push({ label: selectedCategory }); // current page (no link)
        } else {
            crumbs.push({ label: 'All Products' });
        }
        return crumbs;
    })();

    return (
        <PageLayout>
            <PageHeader
                title="Product Catalog"
                subtitle="Browse our extensive collection of IT products and accessories"
                breadcrumbs={catalogBreadcrumbs}
            />

            <section className="jk-catalog">
                <div className="jk-catalog__container">
                    {/* Search and Controls Bar */}
                    <div className="jk-catalog__controls">
                        <div className="jk-catalog__search">
                            <Search size={20} className="jk-catalog__search-icon" />
                            <input
                                type="text"
                                placeholder="Search products, part numbers..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="jk-catalog__search-input"
                            />
                            {searchTerm && (
                                <button
                                    className="jk-catalog__search-clear"
                                    onClick={() => handleSearch('')}
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>

                        <div className="jk-catalog__actions">
                            <button
                                className={`jk-catalog__filter-toggle ${showFilters ? 'active' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <SlidersHorizontal size={18} />
                                <span>Filters</span>
                                {activeFiltersCount > 0 && (
                                    <span className="jk-catalog__filter-badge">{activeFiltersCount}</span>
                                )}
                            </button>

                            <div className="jk-catalog__view-toggle">
                                <button
                                    className={`jk-catalog__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                    aria-label="Grid view"
                                >
                                    <Grid size={18} />
                                </button>
                                <button
                                    className={`jk-catalog__view-btn ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => setViewMode('list')}
                                    aria-label="List view"
                                >
                                    <List size={18} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="jk-catalog__content">
                        {/* Sidebar Filters */}
                        <aside className={`jk-catalog__sidebar ${showFilters ? 'is-visible' : ''}`}>
                            <div className="jk-catalog__sidebar-header">
                                <h3>Filters</h3>
                                {activeFiltersCount > 0 && (
                                    <button className="jk-catalog__reset-btn" onClick={handleResetFilters}>
                                        Reset All
                                    </button>
                                )}
                            </div>

                            {/* Active Filters — show ONLY brand chips.
                                Categories are picked from the top-nav mega-menu
                                and the URL, not from the sidebar, so showing a
                                "Computer Systems X" removable chip was confusing
                                (removing it didn't clear the URL / breadcrumb). */}
                            {selectedBrands.length > 0 && (
                                <div className="jk-catalog__active-filters">
                                    {selectedBrands.map(brand => (
                                        <span key={brand} className="jk-catalog__filter-tag">
                                            {brand}
                                            <button onClick={() => handleBrandSelect(brand)}>
                                                <X size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Categories Filter removed — category picking
                                lives in the top-nav mega-menu, having it also
                                in the sidebar encouraged category × brand
                                combos that return 0 products (e.g. "Displays
                                × Microsoft" — Microsoft doesn't make monitors).
                                Category state + handlers stay so URL-driven
                                single-category flow still works. */}

                            {/* Brands Filter */}
                            <div className="jk-catalog__filter-section">
                                <button
                                    className="jk-catalog__filter-header"
                                    onClick={() => setExpandedFilters(prev => ({
                                        ...prev,
                                        brands: !prev.brands
                                    }))}
                                >
                                    <span>Brands</span>
                                    {expandedFilters.brands ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {expandedFilters.brands && (
                                    <div className="jk-catalog__filter-options">
                                        {brands.length > 0 ? (
                                            brands.map(brand => (
                                                <label key={brand.name} className="jk-catalog__checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBrands.includes(brand.name)}
                                                        onChange={() => handleBrandSelect(brand.name)}
                                                    />
                                                    <span className="jk-catalog__checkbox-mark"></span>
                                                    <span className="jk-catalog__checkbox-label">{brand.name}</span>
                                                    {brand.count != null && (
                                                        <span className="jk-catalog__checkbox-count">({brand.count})</span>
                                                    )}
                                                </label>
                                            ))
                                        ) : (
                                            <p className="jk-catalog__filter-empty">No brands available</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Mobile Apply Button */}
                            <div className="jk-catalog__sidebar-footer">
                                <button
                                    className="jk-btn jk-btn--primary w-full"
                                    onClick={() => setShowFilters(false)}
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </aside>

                        {/* Products Grid */}
                        <div className="jk-catalog__main">
                            {/* Loading State */}
                            {isLoading && <LoadingState />}

                            {/* Error State */}
                            {error && !isLoading && (
                                <ErrorState message={error} onRetry={retry} />
                            )}

                            {/* Products Display */}
                            {!isLoading && !error && (
                                <>
                                    <div className="jk-catalog__results-info">
                                        <span>
                                            Showing <strong>{products.length}</strong> of {totalProducts} products
                                            {currentPage > 1 && ` (Page ${currentPage} of ${totalPages})`}
                                        </span>
                                    </div>

                                    {products.length === 0 ? (
                                        <div className="jk-catalog__empty">
                                            <Package size={48} />
                                            <h3>No products found</h3>
                                            <p>Try adjusting your search or filters</p>
                                            <button className="jk-btn jk-btn--secondary" onClick={handleResetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={`jk-catalog__products jk-catalog__products--${viewMode}`}>
                                                {products.map(product => (
                                                    <Link
                                                        key={product.id}
                                                        to={`/product/${product.id}`}
                                                        className="jk-catalog__product"
                                                    >
                                                        <ProductCardImage product={product} />
                                                        <div className="jk-catalog__product-info">
                                                            <span className="jk-catalog__product-manufacturer">
                                                                {product.manufacturer}
                                                            </span>
                                                            <h3 className="jk-catalog__product-name">{product.name}</h3>
                                                            <span className="jk-catalog__product-part">
                                                                Part #: {product.partNumber}
                                                            </span>
                                                            <div className="jk-catalog__product-footer">
                                                                <span className="jk-catalog__product-price">{product.price}</span>
                                                                <button
                                                                    className={`jk-catalog__product-cart ${isInCart(product.id) ? 'jk-catalog__product-cart--added' : ''}`}
                                                                    disabled={!product.inStock}
                                                                    onClick={(e) => handleAddToCart(e, product)}
                                                                    title={isInCart(product.id) ? 'In cart' : 'Add to cart'}
                                                                >
                                                                    {isInCart(product.id) ? <Check size={18} /> : <ShoppingCart size={18} />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <Pagination
                                                    currentPage={currentPage}
                                                    totalPages={totalPages}
                                                    onPageChange={handlePageChange}
                                                />
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </PageLayout>
    );
};

export default ModernAccessoriesCatalog;
